import { createContext, useContext, useEffect, useState } from "react";
import { STRINGS } from "./i18n";

/* Site-wide language context (EN ↔ DE).
 * Persisted to localStorage. Initial value derives from previous store,
 * else from navigator.language. */

const LangContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (key) => key,
});

const detectInitial = () => {
  /* Phase 1 — URL-parameter-driven auto-localization.
     Priority chain:
      1. ?lang=en or ?lang=de in URL → forced (overrides everything)
      2. localStorage 'kreux-lang' (last manual toggle) → persisted user choice
      3. Default → 'de' (DACH-first)                                          */
  try {
    if (typeof window !== "undefined" && window.location && window.location.search) {
      const p = new URLSearchParams(window.location.search).get("lang");
      if (p) {
        const v = p.toLowerCase();
        if (v === "en" || v === "de") return v;
      }
    }
  } catch (_) { /* noop */ }
  try {
    const stored = localStorage.getItem("kreux-lang");
    if (stored === "en" || stored === "de") return stored;
  } catch (_) { /* noop */ }
  return "de";
};

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState(detectInitial);
  useEffect(() => {
    try { localStorage.setItem("kreux-lang", lang); } catch (_) { /* noop */ }
    try { document.documentElement.setAttribute("lang", lang); } catch (_) { /* noop */ }
  }, [lang]);

  const t = (key) => {
    const dict = STRINGS[lang] || STRINGS.en;
    const val = key.split(".").reduce((acc, part) => (acc == null ? undefined : acc[part]), dict);
    if (val == null) {
      // graceful fallback: try EN, then return the raw key
      const fb = key.split(".").reduce((acc, part) => (acc == null ? undefined : acc[part]), STRINGS.en);
      return fb == null ? key : fb;
    }
    return val;
  };

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
};

export const useLang = () => useContext(LangContext);
